// Se importan los módulos concretos y no el barrel de `adaptivecards`: el
// barrel arrastra el carrusel, que pide el subpath `swiper/modules`, y el
// swiper 6 que usa el proyecto todavía no lo expone.
import { CardElement } from 'adaptivecards/lib/card-elements';
import { GlobalRegistry } from 'adaptivecards/lib/registry';
import { Versions } from 'adaptivecards/lib/serialization';

/**
 * Clase con la que se marcan en el DOM los huecos donde después se montan los
 * gráficos. El renderer de Adaptive Cards es sincrónico y ApexCharts necesita
 * que su contenedor ya esté en el documento para poder medirlo, así que el
 * elemento solo deja el hueco y el componente monta el gráfico al final.
 */
export const CHART_SLOT_CLASS = 'bl-ac-chart';

/**
 * Elementos de gráfico que expone Teams en sus Adaptive Cards. No son parte
 * del esquema estándar: en Teams los dibuja el host, así que en la web hay que
 * registrarlos a mano y renderizarlos por nuestra cuenta.
 */
export const CHART_ELEMENT_TYPES: string[] = [
  'Chart.VerticalBar',
  'Chart.VerticalBar.Grouped',
  'Chart.HorizontalBar',
  'Chart.HorizontalBar.Stacked',
  'Chart.Line',
  'Chart.Area',
  'Chart.Pie',
  'Chart.Donut',
  'Chart.Gauge',
];

/** Definición cruda del gráfico, colgada del hueco que quedó en el DOM */
export interface ChartSlot extends HTMLElement {
  chartDefinition?: Record<string, any>;
}

/**
 * Fabrica el tipo de elemento para un nombre de gráfico concreto.
 *
 * El registro de Adaptive Cards pide un constructor sin argumentos por cada
 * nombre de tipo, y cada instancia tiene que saber devolver el suyo, por eso
 * se genera una clase por tipo en lugar de una sola compartida.
 *
 * @param {string} typeName Nombre del elemento en el json de la tarjeta
 * @returns Constructor del elemento, listo para registrar
 */
function chartElementType(typeName: string): { new(): CardElement } {

  class ChartElement extends CardElement {

    /** Json tal como vino en la tarjeta, es lo que consume el gráfico */
    private definition: Record<string, any> = {};

    getJsonTypeName(): string {
      return typeName;
    }

    protected internalParse(source: any, context: any): void {
      super.internalParse(source, context);
      this.definition = source ?? {};
    }

    protected internalRender(): HTMLElement {
      const slot = document.createElement('div') as ChartSlot;
      slot.className = CHART_SLOT_CLASS;
      slot.chartDefinition = { ...this.definition, type: typeName };

      return slot;
    }
  }

  return ChartElement;
}

/** El registro es global, así que solo hay que poblarlo una vez */
let registered = false;

/**
 * Registra los elementos de gráfico de Teams en el renderer de Adaptive Cards.
 * Sin esto el renderer los descarta con un "unknown element type".
 */
export function registerChartElements(): void {
  if (registered) return;

  CHART_ELEMENT_TYPES.forEach(typeName => {
    GlobalRegistry.elements.register(typeName, chartElementType(typeName), Versions.v1_0);
  });

  registered = true;
}
